import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import { MaskedInput, TextInput } from "components/inputs";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";

import type { LienFormValues } from "../../../LienTypes";

type HearingRequestSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
};

const HearingRequestSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
}: HearingRequestSectionProps) => {
  const hasHearingRequest = getValues("request_for_hearing_due_date") !== null;

  return (
    <>
      <div className="header-primary flex jc-sb ai-c">
        <div className="text-sm text-gray-500">ALJ Hearing</div>
      </div>

      <ControlledDateInput
        control={control}
        name="request_for_hearing_due_date"
        label="Due Date"
        errors={{}}
        allowFutureDates={true}
        onSave={async (value) => {
          if (!value) {
            setValue("request_for_hearing_filed_date", null);
            setValue("omha_appeal_amount", null);
            setValue("omha_appeal_amount_due", null);
            setValue("omha_appeal_number", null);
            setValue("omha_hearing_date", null);
            setValue("omha_response_due_date", null);
            setValue("omha_lien_amount", null);
            setValue("notice_of_decision_date", null);
            setValue("crc_alj_decision_date", null);
            setValue("omha_result_dollar_amount", null);
            setValue("alj_appeal_savings", null);
            await handleFieldChange(null, {
              request_for_hearing_due_date: null,
              request_for_hearing_filed_date: null,
              omha_appeal_amount: null,
              omha_appeal_amount_due: null,
              omha_appeal_number: null,
              omha_hearing_date: null,
              omha_lien_amount: null,
              notice_of_decision_date: null,
              crc_alj_decision_date: null,
              omha_result_dollar_amount: null,
              alj_appeal_savings: null,
            });
          } else {
            await handleFieldChange("request_for_hearing_due_date", value);
          }
        }}
      />

      {hasHearingRequest && (
        <>
          <ControlledDateInput
            control={control}
            name="request_for_hearing_filed_date"
            label="Filed Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) =>
              handleFieldChange("request_for_hearing_filed_date", value)
            }
          />

          <ControlledDateInput
            control={control}
            name="omha_hearing_date"
            label="Hearing Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("omha_hearing_date", value)}
          />

          <ControlledInput
            control={control}
            name="omha_appeal_number"
            label="Appeal Number"
            errors={{}}
            onSave={(value) => handleFieldChange("omha_appeal_number", value)}
            componentType={TextInput}
          />

          <ControlledDateInput
            control={control}
            name="omha_response_due_date"
            label="Response Due Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) =>
              handleFieldChange("omha_response_due_date", value)
            }
          />

          <ControlledInput
            control={control}
            name="omha_lien_amount"
            label="Lien Amount"
            errors={{}}
            onSave={(value) => handleFieldChange("omha_lien_amount", value)}
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="omha_appeal_amount"
            label="Appeal Amount"
            errors={{}}
            onSave={(value) => handleFieldChange("omha_appeal_amount", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <ControlledInput
            control={control}
            name="omha_appeal_amount_due"
            label="Amount Due"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("omha_appeal_amount_due", value)
            }
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <ControlledDateInput
            control={control}
            name="notice_of_decision_date"
            label="Notice of Decision Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) =>
              handleFieldChange("notice_of_decision_date", value)
            }
          />

          <ControlledDateInput
            control={control}
            name="crc_alj_decision_date"
            label="CRC ALJ Decision Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) =>
              handleFieldChange("crc_alj_decision_date", value)
            }
          />

          <ControlledInput
            control={control}
            name="omha_result_dollar_amount"
            label="ALJ Result Dollar Amount"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("omha_result_dollar_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="alj_appeal_savings"
            label="Appeal Savings"
            errors={{}}
            onSave={(value) => handleFieldChange("alj_appeal_savings", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />
        </>
      )}
    </>
  );
};

export default HearingRequestSection;
